import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { LogOut, User, Menu, X } from "lucide-react";
import NotificationBell from "./NotificationBell";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";

const LOGO_URL = "https://uxdesignlabs.com/wp-content/uploads/2024/07/zeel-logo.svg";

export const Navbar = () => {
  const { user, login, logout, loading } = useAuth();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const goToLogin = () => navigate("/login");

  return (
    <nav className="navbar-glass fixed top-0 left-0 right-0 z-50 border-b border-border/40" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center" data-testid="logo-link">
            <img src={LOGO_URL} alt="Zeel" className="h-[28px] w-auto object-contain" />
          </Link>

          <div className="hidden md:flex items-center gap-6">
            <Link to="/products" className="nav-link text-[15px] font-medium text-muted-foreground" data-testid="products-link">
              Products
            </Link>
            <Link to="/routines" className="nav-link text-[15px] font-medium text-muted-foreground" data-testid="routines-link-nav">
              Routines
            </Link>
            <Link to="/upload" className="nav-link text-[15px] font-medium text-muted-foreground" data-testid="upload-link">
              Contribute
            </Link>
            <Link to="/community" className="nav-link text-[15px] font-medium text-muted-foreground" data-testid="community-link">
              Community
            </Link>

            {loading ? (
              <div className="w-8 h-8 rounded-full bg-muted animate-pulse" />
            ) : user ? (
              <>
                <NotificationBell />
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2" data-testid="user-menu-btn">
                      {user.picture ? (
                        <img src={user.picture} alt={user.name} className="w-8 h-8 rounded-full" />
                      ) : (
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <User className="w-4 h-4 text-primary" />
                        </div>
                      )}
                      <span className="text-sm">{user.name?.split(' ')[0]}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem asChild><Link to="/profile" data-testid="profile-link">My Profile</Link></DropdownMenuItem>
                    <DropdownMenuItem asChild><Link to="/my-routines" data-testid="my-routines-link">My Routines</Link></DropdownMenuItem>
                    {user.role === 'admin' && (
                      <DropdownMenuItem asChild><Link to="/admin" data-testid="admin-link">Admin Dashboard</Link></DropdownMenuItem>
                    )}
                    <DropdownMenuItem onClick={logout} data-testid="logout-btn">
                      <LogOut className="w-4 h-4 mr-2" /> Logout
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </>
            ) : (
              <Button onClick={goToLogin} className="rounded-full" data-testid="login-btn">Sign Up</Button>
            )}
          </div>

          <button className="md:hidden p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} data-testid="mobile-menu-btn">
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border/40">
            <div className="flex flex-col gap-4">
              <Link to="/products" className="text-sm font-medium text-muted-foreground" onClick={() => setMobileMenuOpen(false)}>Products</Link>
              <Link to="/routines" className="text-sm font-medium text-muted-foreground" onClick={() => setMobileMenuOpen(false)}>Routines</Link>
              <Link to="/upload" className="text-sm font-medium text-muted-foreground" onClick={() => setMobileMenuOpen(false)}>Contribute</Link>
              <Link to="/community" className="text-sm font-medium text-muted-foreground" onClick={() => setMobileMenuOpen(false)}>Community</Link>
              {user && <Link to="/my-routines" className="text-sm font-medium text-muted-foreground" onClick={() => setMobileMenuOpen(false)}>My Routines</Link>}
              {user ? (
                <>
                  {user.role === 'admin' && <Link to="/admin" className="text-sm font-medium text-muted-foreground" onClick={() => setMobileMenuOpen(false)}>Admin Dashboard</Link>}
                  <button onClick={() => { logout(); setMobileMenuOpen(false); }} className="text-sm font-medium text-muted-foreground text-left">Logout</button>
                </>
              ) : (
                <Button onClick={goToLogin} className="w-full rounded-full">Sign Up</Button>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};
